import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkle, ShieldCheck, WarningCircle } from "@phosphor-icons/react";

export default function SetupPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();

    const [verifying, setVerifying] = useState(true);
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Missing invite token in the URL.");
            setVerifying(false);
            return;
        }
        api.get("/auth/verify-invite", { params: { token } })
            .then((r) => setInvite(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Invalid invite link."))
            .finally(() => setVerifying(false));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/setup-password", { token, password });
            setDone(true);
            toast.success("Password set — you can sign in now.");
            setTimeout(() => navigate("/login"), 1600);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to set password.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
            <div className="w-full max-w-[440px]">
                <div className="flex items-center gap-2 mb-12">
                    <img src="/haevn-mark.png" alt="HAEVN" style={{ height: 22, width: "auto" }} />
                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                        HAEVN Labs · Intelligence
                    </span>
                </div>

                {verifying && (
                    <div data-testid="setup-verifying" className="font-mono text-xs tracking-wider uppercase text-muted-foreground">
                        Verifying invite…
                    </div>
                )}

                {!verifying && error && (
                    <div data-testid="setup-error" className="border border-accent/40 bg-accent/5 p-6 rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <WarningCircle size={18} weight="duotone" className="text-accent" />
                            <h1 className="font-display font-black text-xl tracking-tight">Link unavailable</h1>
                        </div>
                        <p className="editorial text-sm text-muted-foreground leading-relaxed">{error}</p>
                        <p className="editorial text-sm text-muted-foreground mt-3">
                            Ask your administrator to resend the invite — they can do this from the Users panel.
                        </p>
                        <Link to="/login" className="font-mono text-[11px] tracking-wider uppercase text-primary mt-6 inline-block border-b border-primary/40 pb-0.5">
                            Go to sign in
                        </Link>
                    </div>
                )}

                {!verifying && !error && invite && !done && (
                    <>
                        <h1 className="font-display font-black text-3xl tracking-tight mb-2">
                            {invite.purpose === "reset" ? "Choose a new password" : "Welcome to HAEVN Labs"}
                        </h1>
                        <p className="editorial text-sm text-muted-foreground mb-3 leading-relaxed">
                            {invite.purpose === "reset"
                                ? "Set a new password to regain access to your account."
                                : "You've been invited to the HAEVN Labs intelligence system. Set a password to activate your account."}
                        </p>
                        <div className="inline-flex items-center gap-2 mb-8 px-3 py-2 rounded-sm bg-card border border-border">
                            <ShieldCheck size={14} weight="duotone" className="text-primary" />
                            <div className="font-mono text-[11px] tracking-wider">
                                <span className="text-muted-foreground">{invite.email}</span>
                                <span className="mx-2 text-border">·</span>
                                <span className="uppercase">{invite.role}</span>
                            </div>
                        </div>

                        <form onSubmit={submit} className="space-y-5" data-testid="setup-form">
                            <div>
                                <Label className="overline text-[10px] mb-1.5 block">New password (min 8 chars)</Label>
                                <Input
                                    type="password"
                                    autoFocus
                                    minLength={8}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="setup-password-input"
                                    className="rounded-sm"
                                />
                            </div>
                            <div>
                                <Label className="overline text-[10px] mb-1.5 block">Confirm password</Label>
                                <Input
                                    type="password"
                                    minLength={8}
                                    required
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    data-testid="setup-confirm-input"
                                    className="rounded-sm"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="setup-submit-btn"
                                className="w-full rounded-sm"
                            >
                                {submitting ? <Sparkle size={14} className="mr-2 animate-pulse" /> : null}
                                {submitting ? "Setting password…" : "Set password & continue"}
                            </Button>
                        </form>
                    </>
                )}

                {done && (
                    <div data-testid="setup-done" className="border border-emerald-500/30 bg-emerald-500/5 p-6 rounded-sm">
                        <h1 className="font-display font-black text-2xl tracking-tight mb-2">You're set.</h1>
                        <p className="editorial text-sm text-muted-foreground leading-relaxed">
                            Redirecting you to sign in…
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
